/**
 * Simple className merger utility - combines multiple class strings,
 * deduplicates Tailwind classes, handles conditional classes.
 * Implements clsx + twMerge behavior without external dependencies.
 */

type ClassValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | ClassValue[]
  | { [key: string]: boolean | null | undefined }

function flattenClassValues(values: ClassValue[]): string[] {
  const result: string[] = []

  for (const value of values) {
    if (!value && value !== 0) continue

    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed) result.push(trimmed)
    } else if (typeof value === 'number') {
      result.push(String(value))
    } else if (Array.isArray(value)) {
      result.push(...flattenClassValues(value))
    } else if (typeof value === 'object') {
      for (const [key, condition] of Object.entries(value)) {
        if (condition) {
          const trimmed = key.trim()
          if (trimmed) result.push(trimmed)
        }
      }
    }
  }

  return result
}

/**
 * Extracts the Tailwind prefix from a class (e.g., "bg" from "bg-red-500",
 * "text" from "text-sm", "px" from "px-4").
 * Returns the full class if no prefix pattern found.
 */
function getTailwindPrefix(cls: string): string {
  // Handle responsive prefixes like sm:, md:, lg:, dark:, hover:, etc.
  const responsiveMatch = cls.match(/^([a-z]+:)+(.+)$/)
  if (responsiveMatch) {
    const base = cls.split(':').pop() || cls
    const basePrefix = getTailwindBasePrefix(base)
    return cls.substring(0, cls.lastIndexOf(':') + 1) + basePrefix
  }
  return getTailwindBasePrefix(cls)
}

function getTailwindBasePrefix(cls: string): string {
  // Negative values
  const negMatch = cls.match(/^-([a-z]+(?:-[a-z]+)?)(?:-|$)/)
  if (negMatch) return '-' + negMatch[1]

  // Standard prefix-value pattern
  const match = cls.match(/^([a-z]+(?:-[a-z]+)?)(?:-|$)/)
  if (match) return match[1]

  return cls
}

// Tailwind utility groups that should conflict with each other
const TAILWIND_CONFLICT_GROUPS = new Set([
  'bg',
  'text',
  'border',
  'p', 'px', 'py', 'pt', 'pr', 'pb', 'pl',
  'm', 'mx', 'my', 'mt', 'mr', 'mb', 'ml',
  'w', 'h', 'min-w', 'min-h', 'max-w', 'max-h',
  'rounded', 'rounded-t', 'rounded-r', 'rounded-b', 'rounded-l',
  'rounded-tl', 'rounded-tr', 'rounded-br', 'rounded-bl',
  'font',
  'leading',
  'tracking',
  'opacity',
  'z',
  'flex',
  'grid',
  'gap', 'gap-x', 'gap-y',
  'col', 'row',
  'overflow', 'overflow-x', 'overflow-y',
  'shadow',
  'ring',
  'ring-offset',
  'cursor',
  'transition',
  'duration',
  'ease',
  'transform',
  'scale',
  'rotate',
  'translate',
  'skew',
])

function mergeClasses(classes: string[]): string {
  const seen = new Map<string, string>()
  const result: string[] = []

  for (const cls of classes) {
    const prefix = getTailwindPrefix(cls)

    if (TAILWIND_CONFLICT_GROUPS.has(prefix.split(':').pop() || prefix)) {
      if (seen.has(prefix)) {
        // Replace old class with new one
        const index = result.indexOf(seen.get(prefix)!)
        if (index !== -1) result.splice(index, 1)
      }
      seen.set(prefix, cls)
    }

    if (!result.includes(cls)) {
      result.push(cls)
    }
  }

  return result.join(' ')
}

/**
 * Combines class names with Tailwind conflict resolution.
 * Later classes override earlier ones for the same utility group.
 */
export function cn(...inputs: ClassValue[]): string {
  const classes = flattenClassValues(inputs)
  return mergeClasses(classes)
}

export default cn
